import React from 'react';
import { Alert, Box, CircularProgress, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { FunnelDropoffEdgeData, FunnelInsightEdgeMetric, FunnelInsightWarning, FunnelSegmentBucket } from '../types';

interface FunnelInsightsEdgeDrawerProps {
  metric: FunnelInsightEdgeMetric | null;
  drilldown?: FunnelDropoffEdgeData | null;
  loading?: boolean;
  error?: string | null;
  warnings?: FunnelInsightWarning[];
  open: boolean;
  onClose: () => void;
}

export const FunnelInsightsEdgeDrawer: React.FC<FunnelInsightsEdgeDrawerProps> = ({
  metric,
  drilldown,
  loading = false,
  error = null,
  warnings = [],
  open,
  onClose,
}) => {
  const displayMetric = drilldown?.edge ?? metric;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 340, p: 2 },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Edge details</Typography>
        <IconButton aria-label="close edge details" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {displayMetric && (
        <Stack spacing={1.5} data-testid="funnel-insights-edge-drawer">
          {loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading drilldown</Typography>
            </Stack>
          )}
          {error && <Alert severity="warning">{error}</Alert>}
          <MetricRow label="Transitions" value={displayMetric.transitionCount} />
          <MetricRow label="Journeys" value={displayMetric.uniqueJourneys} />
          <MetricRow label="Forward" value={displayMetric.forwardTransitionCount} />
          <MetricRow label="Back" value={displayMetric.backTransitionCount} />
          <MetricRow label="Jump" value={displayMetric.jumpTransitionCount} />
          {drilldown?.avgTimeToNextSec !== null && drilldown?.avgTimeToNextSec !== undefined && (
            <MetricRow label="Avg next step" value={`${drilldown.avgTimeToNextSec}s`} />
          )}
          {drilldown && (
            <>
              <Divider />
              <SegmentList label="Countries" buckets={drilldown.segments.countries} />
              <SegmentList label="Devices" buckets={drilldown.segments.devices} />
              <SegmentList label="Variants" buckets={drilldown.segments.variants} />
              <SegmentList label="Attribution" buckets={drilldown.segments.attributionStatuses} />
            </>
          )}
          {warnings.map(warning => (
            <Alert key={warning.code} severity="info">{warning.message}</Alert>
          ))}
          <Box sx={{ pt: 1, color: 'text.secondary', fontSize: 12, wordBreak: 'break-all' }}>
            {displayMetric.canvasTransitionId}
          </Box>
        </Stack>
      )}
    </Drawer>
  );
};

const MetricRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Stack>
);

const SegmentList: React.FC<{ label: string; buckets: FunnelSegmentBucket[] }> = ({ label, buckets }) => {
  if (!buckets.length) return null;

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {buckets.slice(0, 4).map(bucket => (
        <MetricRow key={`${label}:${bucket.value}`} label={bucket.value} value={bucket.count} />
      ))}
    </Stack>
  );
};
